import { SpeechRecognition as CapacitorSpeech } from '@capacitor-community/speech-recognition';

export interface SpeechService {
  isAvailable(): Promise<boolean>;
  start(options?: { language?: string; partialResults?: boolean }): Promise<void>;
  stop(): Promise<void>;
  addListener(event: 'partialResults', callback: (data: { matches?: string[] }) => void): Promise<() => void>;
}

// Detect if running in Capacitor native environment
function isNative(): boolean {
  return typeof (window as unknown as Record<string, unknown>).Capacitor !== 'undefined' && 
         (window as unknown as Record<string, unknown>).Capacitor !== null;
}

class CapacitorSpeechService implements SpeechService {
  async isAvailable(): Promise<boolean> {
    try {
      const result = await CapacitorSpeech.available();
      return result.available;
    } catch {
      return false;
    }
  }

  async start(options?: { language?: string; partialResults?: boolean }): Promise<void> {
    await CapacitorSpeech.start({
      language: options?.language || 'en-US',
      maxResults: 1,
      partialResults: options?.partialResults ?? true,
      popup: true,
    });
  }

  async stop(): Promise<void> {
    await CapacitorSpeech.stop();
  }

  async addListener(
    event: 'partialResults',
    callback: (data: { matches?: string[] }) => void
  ): Promise<() => void> {
    const listener = await CapacitorSpeech.addListener(event, callback);
    return () => listener.remove();
  }
}

class WebSpeechService implements SpeechService {
  private recognition: SpeechRecognition | null = null;
  private partialCallback: ((data: { matches?: string[] }) => void) | null = null;

  async isAvailable(): Promise<boolean> {
    const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
    return !!SpeechRecognitionAPI;
  }

  async start(options?: { language?: string; partialResults?: boolean }): Promise<void> {
    const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognitionAPI) throw new Error('Speech recognition not supported');

    this.recognition = new SpeechRecognitionAPI();
    this.recognition.continuous = false;
    this.recognition.interimResults = options?.partialResults ?? true;
    this.recognition.lang = options?.language || 'en-US';

    this.recognition.onresult = (event: SpeechRecognitionEvent) => {
      if (!this.partialCallback) return;
      
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        this.partialCallback({ matches: [result[0].transcript] });
      }
    };

    this.recognition.start();
  }

  async stop(): Promise<void> {
    if (this.recognition) {
      this.recognition.stop();
      this.recognition = null;
    }
  }

  async addListener(
    _event: 'partialResults',
    callback: (data: { matches?: string[] }) => void
  ): Promise<() => void> {
    this.partialCallback = callback;
    return () => {
      this.partialCallback = null;
    };
  }
}

export const speechService: SpeechService = isNative()
  ? new CapacitorSpeechService()
  : new WebSpeechService();
